import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { Fragment, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { preload } from 'swr';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { useEmploymentProposals } from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { getManagerIds } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyMuiButton } from '~/legacy-components/LegacyForms';

import {
  Employment,
  ScheduleEmployment,
  ScheduleEmploymentDetail,
} from './components';
import { useScheduleChanges } from './useScheduleChanges';
import { useSxStyles } from './useStyles';

type Props = {
  handleCloseDialog: () => void;
  handleOpenDialog: () => void;
  open: boolean;
  empId: number;
};

export const ScheduleChanges = (props: Props) => {
  const { handleCloseDialog, handleOpenDialog, open, empId } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const proposalsData = useEmploymentProposals({
    employeeId: empId,
  });

  const {
    scheduleFormMethods,
    onSave,
    additionalCostCenters,
    setAdditionalCostCenters,
    pendingData,
    restrictField,
    scheduleFieldArrayMethods,
    setDeletedEmployments,
    submitting,
    onDialogClose,
    setChangedStartDate,
    allResponsibleHRIds,
    resetFormAfterProposalDecision,
    hasDataIssue,
  } = useScheduleChanges(empId, handleCloseDialog, proposalsData);

  const { remove, append, fields } = scheduleFieldArrayMethods;
  const { control, getValues, watch } = scheduleFormMethods;
  useEffect(() => {
    preload('managers', () => getManagerIds());
  }, []);

  const AddNewEmployment = ({ index }: { index: number }) => {
    //@ts-ignore
    useWatch({ control, name: 'schedule' });

    const lastItemIndex = fields.length - 1;
    const lastItemEndDate = getValues(
      `schedule.${lastItemIndex}.employment.fEmplLastDateEmployment`,
    );
    const lastItemTerminationReason = getValues(
      `schedule.${lastItemIndex}.employment.fEmplTerminationReason`,
    );
    if (
      !lastItemEndDate ||
      !lastItemTerminationReason ||
      !(fields.length - 1 === index)
    ) {
      return <></>;
    }
    return (
      <Button
        component="span"
        sx={{ textTransform: 'none' }}
        fullWidth
        startIcon={<AddIcon />}
        variant="contained"
        color="secondary"
        onClick={() => {
          const allValues = scheduleFormMethods.getValues();
          const lastEmploymentValues =
            allValues.schedule[allValues.schedule.length - 1];
          append({
            ...lastEmploymentValues,
            empPosition: {
              ...lastEmploymentValues.empPosition!,
              fInternalPositionStartDate: moment(
                lastEmploymentValues.employment?.fEmplLastDateEmployment,
              ).add(1, 'day'),
            },
            employmentDetails: {
              ...lastEmploymentValues.employmentDetails!,
              fEmplDetailStartDate: moment(
                lastEmploymentValues.employment?.fEmplLastDateEmployment,
              ).add(1, 'day'),
              fEmplId: undefined,
            },
            employment: {
              fEmplInfo: lastEmploymentValues.employment?.fEmplInfo || '',
              fEmplTrialPeriod: null,
              fEmplTrialPeriodEndDate: null,
              fEmplPlannedEndDate: null,
              fEmplLastDateEmployment: null,
              fEmplTerminationDate: null,
              fEmplDateStart: moment(
                lastEmploymentValues.employment?.fEmplLastDateEmployment,
              ).add(1, 'day'),
              fEmplDateArrival: moment(
                lastEmploymentValues.employment?.fEmplLastDateEmployment,
              ).add(1, 'day'),
              fEmplTerminationReason: null,
              fEmplFixedTermReason: null,
              fEmplType: lastEmploymentValues.employment?.fEmplType ?? null,
            },
          });
        }}
      >
        {t('laAddNewEmployment')}
      </Button>
    );
  };
  const RemoveButton = ({ index }: { index: number }) => {
    //@ts-ignore
    useWatch({ control, name: 'schedule' });

    if (!(fields.length - 1 === index) || index === 0) {
      return <></>;
    }
    return (
      <Button
        fullWidth
        variant="contained"
        sx={{ textTransform: 'none', mt: 1 }}
        onClick={() => {
          remove(index);
          const emplId = pendingData?.[index]?.employmentDetails?.fEmplId;
          if (emplId) {
            setDeletedEmployments((prevState) => [...prevState, emplId]);
          }
        }}
        startIcon={<DeleteIcon />}
      >
        {t('laDeleteEmployment')}
      </Button>
    );
  };

  const dialogOnClose = () => {
    scheduleFormMethods.reset();
    onDialogClose();
  };

  const hasFormErrors: boolean =
    Object.keys(scheduleFormMethods.formState.errors).length > 0;

  /** Quickfix Workaround: isTerminationFieldsInSync
   * FormState doesn't update errors state when used via UI2 Library.
   * Checking common error condition manually before initial submit.
   * If Employment End Date is defined, then Termination Reason should be also.
   */
  const watchTerminations = fields.map((_, index) => {
    return {
      //@ts-ignore
      endDate: watch(`schedule.${index}.employment.fEmplLastDateEmployment`),
      reason: watch(`schedule.${index}.employment.fEmplTerminationReason`),
    };
  });
  const isTerminationFieldsInSync =
    watchTerminations
      .map((item) => {
        return !item.endDate || (!!item.endDate && !!item.reason);
      })
      .filter((item) => !item).length === 0;

  return (
    <>
      <LegacyMuiButton
        onClick={() => handleOpenDialog()}
        sx={sxStyles.btnLeftMargin}
        data-testid="scheduleButton"
      >
        {t('laSchedule')}
      </LegacyMuiButton>

      <DraggableDialog
        open={open}
        onClose={dialogOnClose}
        maxWidth={'lg'}
        PaperProps={{ sx: sxStyles.dialogPaper }}
      >
        <SolaForceDialogTitle
          icon={<LegacyFAIcon FAIcon={LegacyFAIcons.CalendarPlus} />}
          onClose={dialogOnClose}
        >
          {t('laSchedule')}
        </SolaForceDialogTitle>
        <Divider />
        <DialogContent>
          {hasDataIssue ? (
            <Alert severity={'error'}>{t('laGeneralError')}</Alert>
          ) : (
            <Stack spacing={2}>
              {fields.map((item, index) => {
                const titleEndDate = item.employment?.fEmplLastDateEmployment
                  ? moment(
                      item.employment.fEmplLastDateEmployment,
                      scheduledDateFormats.dashFormat,
                    ).format(scheduledDateFormats.dotFormat)
                  : t('laContinuous');
                const title = item.employment?.fEmplDateStart
                  ? `${moment(
                      item.employment.fEmplDateStart,
                      scheduledDateFormats.dashFormat,
                    ).format(scheduledDateFormats.dotFormat)} - ${titleEndDate}`
                  : t('laNewEmployment');
                return (
                  <Fragment key={item.id}>
                    <Accordion defaultExpanded={index === 0}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        sx={{ backgroundColor: '#eee' }}
                      >
                        <Typography>
                          {index === 0 ? `${title} (${t('laCurrent')})` : title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={5}>
                          <Stack spacing={4}>
                            <ScheduleEmployment
                              index={index}
                              restrictField={restrictField}
                              scheduleFormMethods={scheduleFormMethods}
                              additionalCostCenters={
                                additionalCostCenters?.[index] || []
                              }
                              setAdditionalCostCenters={
                                setAdditionalCostCenters
                              }
                              empPositionPending={pendingData}
                              setChangedStartDate={setChangedStartDate}
                              allResponsibleHRIds={allResponsibleHRIds}
                              empId={empId}
                              resetFormAfterProposalDecision={
                                resetFormAfterProposalDecision
                              }
                              proposalsData={proposalsData}
                            />
                            <Divider />
                            <ScheduleEmploymentDetail
                              index={index}
                              scheduleFormMethods={scheduleFormMethods}
                              empId={empId}
                              employmentDetailPending={pendingData}
                              setChangedStartDate={setChangedStartDate}
                              allResponsibleHRIds={allResponsibleHRIds}
                              resetFormAfterProposalDecision={
                                resetFormAfterProposalDecision
                              }
                              proposalsData={proposalsData}
                            />
                            <Divider />
                            <Employment
                              restrictField={restrictField}
                              scheduleFormMethods={scheduleFormMethods}
                              employmentDataPending={
                                pendingData?.[index]?.employment
                              }
                              index={index}
                            />
                          </Stack>
                        </Stack>
                        <RemoveButton index={index} />
                      </AccordionDetails>
                    </Accordion>
                    {!restrictField.managerCantRehire && (
                      <AddNewEmployment index={index} />
                    )}
                  </Fragment>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          {hasFormErrors && (
            <Typography color={'error'} sx={sxStyles.formErrorMessage}>
              {t('formValidationFailed')}
            </Typography>
          )}

          <Button
            type={'submit'}
            onClick={onSave}
            disabled={
              submitting ||
              hasFormErrors ||
              !isTerminationFieldsInSync ||
              hasDataIssue
            }
            startIcon={
              submitting ? (
                <CircularProgress size={20} />
              ) : (
                <LegacyFAIcon FAIcon={LegacyFAIcons.Save} />
              )
            }
          >
            {t('save')}
          </Button>
          <Button
            onClick={dialogOnClose}
            startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Close} />}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </DraggableDialog>
    </>
  );
};
